import type { ComponentType, ReactNode, SVGProps } from 'react';
import { cn } from '@/lib/utils';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface VerticalSubNavItem {
  /** Stable id — also used as testid suffix. */
  id: string;
  label: ReactNode;
  Icon?: IconComponent;
  /** Optional right-side badge/count/status. */
  meta?: ReactNode;
  /** When true, the item is filtered out of the rendered list. */
  hidden?: boolean;
  /** Disables click + adds a11y disabled state. */
  disabled?: boolean;
}

interface Props {
  items: VerticalSubNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  className?: string;
  /** aria-label for the nav landmark. Default "Sections". */
  label?: string;
}

// Vertical list of internal-page nav items with active-state styling. Used
// on detail pages (admin customer detail today; every detail page in 5B) to
// replace the horizontal shadcn Tabs pattern.
//
// Hidden items are filtered out entirely — pass `hidden: true` when a nav
// item's target feature is gated off (e.g. Uploads behind
// VITE_FEATURE_DATA_UPLOADS). This matches Chunk 4's convention of building
// nav lazily rather than rendering-and-hiding.
export function VerticalSubNav({
  items,
  activeId,
  onSelect,
  className,
  label = 'Sections',
}: Props) {
  const visible = items.filter((i) => !i.hidden);
  return (
    <nav
      aria-label={label}
      data-testid="vertical-sub-nav"
      className={cn('flex flex-col gap-1', className)}
    >
      {visible.map((item) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-disabled={item.disabled}
            disabled={item.disabled}
            onClick={() => onSelect(item.id)}
            data-testid={`vertical-sub-nav-item-${item.id}`}
            data-active={active}
            className={cn(
              'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-accent font-medium text-accent-foreground'
                : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
              item.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {item.Icon && <item.Icon className="h-4 w-4" aria-hidden="true" />}
            <span className="flex-1 truncate text-left">{item.label}</span>
            {item.meta}
          </button>
        );
      })}
    </nav>
  );
}
